import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import type { TimeValidationError } from '@mui/x-date-pickers/models';
import type { PickerChangeHandlerContext } from '@mui/x-date-pickers/models/pickers';
import { he } from 'date-fns/locale';
import type { FieldAppearance } from '../fieldAppearance';
import { SmPickerField, type SmPickerFieldProps } from '../shared/SmPickerField';
import { formatTimePickerChange, toPickerTime } from './utils';

const DEFAULT_FORMAT = 'HH:mm';

export type TimePickerBaseProps = {
  label?: string;
  /** @default 'default' */
  appearance?: FieldAppearance;
  placeholder?: string;
  /** @default true */
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minTime?: Date;
  maxTime?: Date;
  format?: string;
};

type StringTimePickerProps = TimePickerBaseProps & {
  value: string;
  onChange: (value: string | null) => void;
};

type DateTimePickerProps = TimePickerBaseProps & {
  value: Date | null;
  onChange: (value: Date | null) => void;
};

export type TimePickerProps = StringTimePickerProps | DateTimePickerProps;

function TimePickerImpl({
  label,
  appearance = 'default',
  placeholder,
  fullWidth = true,
  disabled,
  error,
  helperText,
  minTime,
  maxTime,
  format = DEFAULT_FORMAT,
  value,
  onChange
}: TimePickerProps) {
  const updateValue = (date: Date | null) => {
    if (typeof value === 'string') {
      (onChange as StringTimePickerProps['onChange'])(formatTimePickerChange(value, date));
    } else {
      (onChange as DateTimePickerProps['onChange'])(formatTimePickerChange(value, date));
    }
  };

  const handleChange = (date: Date | null, context: PickerChangeHandlerContext<TimeValidationError>) => {
    if (context.validationError == null) {
      updateValue(date);
    }
  };

  const textFieldProps = {
    label,
    fullWidth,
    error,
    helperText,
    appearance,
    placeholder,
    endIcon: <ScheduleOutlinedIcon />
  } as SmPickerFieldProps;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <MuiTimePicker
        label={label}
        value={toPickerTime(value)}
        onChange={handleChange}
        onAccept={updateValue}
        disabled={disabled}
        minTime={minTime}
        maxTime={maxTime}
        format={format}
        ampm={false}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock
        }}
        slotProps={{ textField: textFieldProps }}
        slots={{ textField: SmPickerField }}
      />
    </LocalizationProvider>
  );
}

export function TimePicker(props: StringTimePickerProps): JSX.Element;
export function TimePicker(props: DateTimePickerProps): JSX.Element;
export function TimePicker(props: TimePickerProps): JSX.Element {
  return TimePickerImpl(props);
}
